import { useState, useEffect, useContext, createContext } from 'react';
import { safeStorage } from '../utils/safeStorage';
import { useOrganizationTheme } from './useOrganizationTheme'; // Multi-tenant theme support

const THEME_STORAGE_KEY = 'evv-theme';

// Theme context
const ThemeContext = createContext();

// The one place <html> is put into light or dark mode, matching the inline
// script in index.html. Both classes matter: index.css applies the dark colour
// variables under `@media (prefers-color-scheme: dark) :root:not(.light)`, so
// on a computer set to dark mode a light page must carry `light`, not merely
// lack `dark`.
function applyThemeToDocument(effectiveTheme) {
  const root = document.documentElement;
  const isDark = effectiveTheme === 'dark';

  root.classList.toggle('dark', isDark);
  root.classList.toggle('light', !isDark);
  root.style.colorScheme = isDark ? 'dark' : 'light';

  // Update theme-color meta tag for mobile browsers
  let themeColorMeta = document.querySelector('meta[name="theme-color"]:not([media])');
  if (!themeColorMeta) {
    themeColorMeta = document.createElement('meta');
    themeColorMeta.name = 'theme-color';
    document.head.appendChild(themeColorMeta);
  }
  themeColorMeta.content = isDark ? '#121212' : '#F5F7FA';
}

// Theme provider component
export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState('system');
  const [effectiveTheme, setEffectiveTheme] = useState('light');
  
  // Multi-tenant organization theme support
  const orgTheme = useOrganizationTheme();

  // Initialize theme from localStorage or default to system, and follow a
  // theme chosen in another tab.
  useEffect(() => {
    const savedTheme = safeStorage.getItem(THEME_STORAGE_KEY) || 'system';
    setTheme(savedTheme);

    const handleStorageChange = (e) => {
      if (e.key === THEME_STORAGE_KEY) setTheme(e.newValue || 'system');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Update effective theme based on theme setting and system preference
  useEffect(() => {
    const updateEffectiveTheme = () => {
      if (theme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        setEffectiveTheme(systemTheme);
      } else {
        setEffectiveTheme(theme);
      }
    };

    updateEffectiveTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        updateEffectiveTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Apply theme and organization colors to document
  useEffect(() => {
    const root = document.documentElement;

    applyThemeToDocument(effectiveTheme);

    // Apply organization colors as CSS variables
    if (orgTheme.theme) {
      root.style.setProperty('--org-primary', orgTheme.getColor('primary'));
      root.style.setProperty('--org-secondary', orgTheme.getColor('secondary'));
      root.style.setProperty('--org-accent', orgTheme.getColor('accent'));
      // The tab title belongs to usePageTitle; resetting it here on every
      // theme change wiped the page's name.
    }
  }, [effectiveTheme, orgTheme]);

  const setThemeMode = (newTheme) => {
    setTheme(newTheme);
    safeStorage.setItem(THEME_STORAGE_KEY, newTheme); // never throws: safeStorage catches internally
  };

  const value = {
    theme,
    effectiveTheme,
    setTheme: setThemeMode,
    isDark: effectiveTheme === 'dark',
    isLight: effectiveTheme === 'light',
    // Multi-tenant organization theme data
    organization: orgTheme.theme,
    organizationSlug: orgTheme.organizationSlug,
    hasFeature: orgTheme.hasFeature,
    getColor: orgTheme.getColor,
    isLoading: orgTheme.isLoading
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Hook to use theme context
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Theme hook for the theme toggles. Inside a ThemeProvider it IS the
// provider's theme: a separate copy left ThemeProvider (and every page reading
// useTheme().isDark) on the old theme after a toggle. Outside one it keeps its
// own state and applies it to the document the same way.
export function useSystemTheme() {
  const context = useContext(ThemeContext);
  const standalone = useStandaloneTheme(!context);
  if (!context) return standalone;

  const { theme, effectiveTheme, setTheme, isDark, isLight } = context;
  return { theme, effectiveTheme, setTheme, isDark, isLight };
}

function useStandaloneTheme(appliesToDocument) {
  const [theme, setTheme] = useState('system');
  const [effectiveTheme, setEffectiveTheme] = useState('light');

  useEffect(() => {
    const savedTheme = safeStorage.getItem(THEME_STORAGE_KEY) || 'system';
    setTheme(savedTheme);

    const updateEffectiveTheme = () => {
      if (savedTheme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        setEffectiveTheme(systemTheme);
      } else {
        setEffectiveTheme(savedTheme);
      }
    };

    updateEffectiveTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', updateEffectiveTheme);
    
    // Listen for theme changes from other tabs
    const handleStorageChange = (e) => {
      if (e.key === THEME_STORAGE_KEY) {
        const newTheme = e.newValue || 'system';
        setTheme(newTheme);
        updateEffectiveTheme();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    return () => {
      mediaQuery.removeEventListener('change', updateEffectiveTheme);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Apply theme to document (ThemeProvider does it when there is one)
  useEffect(() => {
    if (appliesToDocument) applyThemeToDocument(effectiveTheme);
  }, [effectiveTheme, appliesToDocument]);

  const setThemeMode = (newTheme) => {
    setTheme(newTheme);
    safeStorage.setItem(THEME_STORAGE_KEY, newTheme); // never throws: safeStorage catches internally

    // Update effective theme immediately
    if (newTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setEffectiveTheme(systemTheme);
    } else {
      setEffectiveTheme(newTheme);
    }
  };

  return {
    theme,
    effectiveTheme,
    setTheme: setThemeMode,
    isDark: effectiveTheme === 'dark',
    isLight: effectiveTheme === 'light'
  };
}
